import type { CultureCode } from '@agency';
import type { EnvironmentName } from '../../../../../environments/environment.types';
import { resolveCostaExtraOrigin } from '../../../costa-extra';

export type SsoEnvironment = EnvironmentName;

interface SsoRoute {
  culture: CultureCode;
  frontendHosts: Record<SsoEnvironment, string>;
}

const LOCAL_SSO_PAGE = 'OtoLoginDigitalCatalogLocal.aspx'; // <== sostituire con ↓
// const LOCAL_SSO_PAGE = 'TicketingSSOLocal.aspx'; // <== quando lo attiveranno
const PREVIEW_SSO_PAGE = 'TicketingSSO.aspx';
const PRODUCTION_SSO_PAGE = 'TicketingSSO.aspx';

const SSO_ROUTES: readonly SsoRoute[] = [
  {
    culture: 'it-IT',
    frontendHosts: {
      local: 'ticketing.local.costaextra.it',
      preview: 'ticketing.training.costaextra.it',
      production: 'ticketing.costaextra.it',
    },
  },
  {
    culture: 'fr-FR',
    frontendHosts: {
      local: 'ticketing.local.costaextra.fr',
      preview: 'ticketing.training.costaextra.fr',
      production: 'ticketing.costaextra.fr',
    },
  },
  {
    culture: 'es-ES',
    frontendHosts: {
      local: 'ticketing.local.costaextra.es',
      preview: 'ticketing.training.costaextra.es',
      production: 'ticketing.costaextra.es',
    },
  },
  {
    culture: 'pt-PT',
    frontendHosts: {
      local: 'ticketing.local.costaextra.pt',
      preview: 'ticketing.training.costaextra.pt',
      production: 'ticketing.costaextra.pt',
    },
  },
  {
    culture: 'de-DE',
    frontendHosts: {
      local: 'ticketing.local.costaextra.de',
      preview: 'ticketing.training.costaextra.de',
      production: 'ticketing.costaextra.de',
    },
  },
  {
    culture: 'de-AT',
    frontendHosts: {
      local: 'ticketing.local.costaextra.at',
      preview: 'ticketing.training.costaextra.at',
      production: 'ticketing.costaextra.at',
    },
  },
  {
    culture: 'it-CH',
    frontendHosts: {
      local: 'ticketing.local.costaextra.ch',
      preview: 'ticketing.training.costaextra.ch',
      production: 'ticketing.costaextra.ch',
    },
  },
  {
    culture: 'en-BZ',
    frontendHosts: {
      local: 'ticketing.local.int.costaextra.com',
      preview: 'ticketing.training.int.costaextra.com',
      production: 'ticketing.int.costaextra.com',
    },
  },
  {
    culture: 'nl-BE',
    frontendHosts: {
      local: 'ticketing.local.costaextra.be',
      preview: 'ticketing.training.costaextra.be',
      production: 'ticketing.costaextra.be',
    },
  },
  {
    culture: 'en-GB',
    frontendHosts: {
      local: 'ticketing.local.costaextra.uk',
      preview: 'ticketing.training.costaextra.uk',
      production: 'ticketing.costaextra.uk',
    },
  },
  {
    culture: 'en-US',
    frontendHosts: {
      local: 'ticketing.local.booking.costaextra.com',
      preview: 'ticketing.training.booking.costaextra.com',
      production: 'ticketing.booking.costaextra.com',
    },
  },
  {
    culture: 'es-AR',
    frontendHosts: {
      local: 'ticketing.local.booking.costaextra.com.ar',
      preview: 'ticketing.training.booking.costaextra.com.ar',
      production: 'ticketing.booking.costaextra.com.ar',
    },
  },
  {
    culture: 'es-MX',
    frontendHosts: {
      local: 'ticketing.local.booking.costaextra.com.mx',
      preview: 'ticketing.training.booking.costaextra.com.mx',
      production: 'ticketing.booking.costaextra.com.mx',
    },
  },
  {
    culture: 'pt-BR',
    frontendHosts: {
      local: 'ticketing.local.booking.costaextra.com.br',
      preview: 'ticketing.training.booking.costaextra.com.br',
      production: 'ticketing.booking.costaextra.com.br',
    },
  },
];

export function resolveSsoLoginUrl(environment: string, hostname: string): string {
  if (!isSsoEnvironment(environment)) {
    throw new Error(`Unsupported SSO environment: ${environment}`);
  }

  const normalizedHostname = hostname.trim().toLowerCase();
  const route = SSO_ROUTES.find(
    ({ frontendHosts }) => frontendHosts[environment] === normalizedHostname,
  );
  if (!route) {
    throw new Error(`Unsupported Smarty frontend hostname: ${hostname}`);
  }

  const ssoOrigin = resolveCostaExtraOrigin(environment, route.culture);
  const ssoPage =
    environment === 'local'
      ? LOCAL_SSO_PAGE
      : environment === 'preview'
        ? PREVIEW_SSO_PAGE
        : PRODUCTION_SSO_PAGE;

  return `${ssoOrigin}/CostaClick/${route.culture}/LoggedOn/Pages/${ssoPage}`;
}

function isSsoEnvironment(value: string): value is SsoEnvironment {
  return value === 'local' || value === 'preview' || value === 'production';
}
